import { format } from "date-fns";
import ExcelJS from "exceljs";
import type { FunctionComponent } from "react";

import DownloadExcelButton from "~/components/buttons/DownloadExcelButton";
import { DEFAULT_EXCEL_TABLE_STYLE } from "~/constants/excelConsts.ts";
import {
	pendingInvoicesExcelColumnDefs,
} from "~/modules/billing/components/PendingInvoicesIndexView/components/DownloadPendingInvoicesTableDataButton/pendingInvoicesExcelColumnDefs.ts";
import type { UnprocessedInvoicesIndexTableData } from "~/modules/billing/components/unprocessedInvoicesTableTypes.ts";
import type { DateRangeNullable } from "~/types/dateAndTimeTypes.ts";
import { formatSmartDateRange } from "~/utils/dateAndTimeUtils.ts";
import { byObjectProperty } from "~/utils/sortFunctions.ts";

type Props = {
	deliveryPeriodFilter: DateRangeNullable,
	filteredTableData?: UnprocessedInvoicesIndexTableData[],
	search: string;
};

const DownloadPendingInvoicesTableDataButton: FunctionComponent<Props> = ({
	deliveryPeriodFilter,
	filteredTableData,
	search,
}) => {
	const handleDownload = async () => {
		if (!filteredTableData || filteredTableData.length === 0) {
			return;
		}

		const workbook = new ExcelJS.Workbook();
		const worksheet = workbook.addWorksheet(`Offene Rechnungen`);

		const preprocessedTableData = filteredTableData.sort(byObjectProperty("companyDisplayName")).map((data) => {
			return [
				data.companyDisplayName,
				data.projectNumberWithPhaseNumber,
				data.projectTitle,
				data.invoiceTotalCents / 100,
				formatSmartDateRange(data.deliveryStartDate, data.deliveryEndDate, {
					month: "MMM",
					year: " yy",
				}),
				data.comment,
			];
		});

		if (deliveryPeriodFilter.startDate && deliveryPeriodFilter.endDate) {
			worksheet.addRow(["Zeitraum", formatSmartDateRange(deliveryPeriodFilter.startDate, deliveryPeriodFilter.endDate, {
				month: "MMM",
				year: " yy",
			})]);
		}

		if (search) {
			worksheet.addRow(["Suchfilter", search]);
		}

		worksheet.addRow(["Erstellt am", format(new Date(), "dd.MM.yyyy HH:mm") + " h"]);

		// Add an empty row for spacing
		worksheet.addRow([]);

		// Add table headers
		const headerRowNumber = worksheet.rowCount + 1;
		const headers = pendingInvoicesExcelColumnDefs.map(col => col.header);
		worksheet.addRow(headers);

		// Prepare usersActiveStaffings rows
		const rows = preprocessedTableData;

		// Create an Excel table
		const tableEndRowNumber = headerRowNumber + rows.length;
		const tableEndColumnLetter = String.fromCharCode(64 + headers.length);
		const tableName = "TimeTrackingsTable";
		const tableRef = `A${headerRowNumber}:${tableEndColumnLetter}${tableEndRowNumber}`;

		const table = worksheet.addTable({
			name: tableName,
			ref: tableRef,
			columns: pendingInvoicesExcelColumnDefs.map(col => ({ name: col.header })),
			rows: rows,
			style: {
				theme: DEFAULT_EXCEL_TABLE_STYLE,
				showRowStripes: true,
			},
			totalsRow: false,
		});

		// Make table sortable and filterable
		// @ts-expect-error type definitions are missing these properties
		table.table.columns.forEach(column => {
			column.filterButton = true;
		});

		// Apply cell formatting
		pendingInvoicesExcelColumnDefs.forEach((colDef, index) => {
			const column = worksheet.getColumn(index + 1);
			let numFmt: string | undefined;
			switch (colDef.type) {
				case "currency":
					numFmt = "#,##0.00 \"€\"";
					break;
				case "date":
					numFmt = "dd.mm.yyyy";
					break;
				case "number":
					numFmt = "#,##0.00";
					break;
			}
			if (numFmt) {
				column.eachCell({ includeEmpty: true }, cell => {
					cell.numFmt = numFmt!;
				});
			}
		});

		// Add sum row with formulas
		const sumRow = worksheet.addRow([
			"", "", "Summe",
			{ formula: `SUBTOTAL(9,D${headerRowNumber + 1}:D${tableEndRowNumber})` },
		]);
		sumRow.font = { bold: true };


		// Format sum and total days rows
		sumRow.eachCell((cell, colNumber) => {
			const column = pendingInvoicesExcelColumnDefs[colNumber - 1];
			if (column) {
				switch (column.type) {
					case "currency":
						cell.numFmt = "#,##0.00 \"€\"";
						break;
			}
			cell.alignment = { vertical: "top", wrapText: true };
		}});


		// Auto-fit columns
		worksheet.columns.forEach((column) => {
			let maxLength = 0;
			// @ts-expect-error - TS doesn't know about the includeEmpty option
			column.eachCell({ includeEmpty: true }, (cell) => {
				const cellLength = cell.value ? cell.value.toString().length : 10;
				if (cellLength > maxLength) {
					maxLength = cellLength;
				}
			});
			if (column) {
				column.width = Math.min(maxLength + 2, 30);
			}
		});

		// Generate and download the Excel file
		const buffer = await workbook.xlsx.writeBuffer();
		const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `Offene Rechnungen.xlsx`;
		a.click();
		window.URL.revokeObjectURL(url);
	};

	return <DownloadExcelButton onClick={handleDownload}
								buttonText=""
								tooltipText="Aktuellen Tabelleninhalt als Excel-Datei herunterladen"
								disabled={!filteredTableData || filteredTableData.length === 0} />;
};

export default DownloadPendingInvoicesTableDataButton;